import { formatDate } from "@angular/common";
import { Directive, ElementRef, Input } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Directive({
    selector: "[inputDate]",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: InputDateDirective,
        },
    ],
})
export class InputDateDirective implements ControlValueAccessor {
    @Input() formControlName;

    onChange(value: Date | undefined) {}

    onTouched() {}

    constructor(private _elementRef: ElementRef) {
        this._listener();
    }

    writeValue(value: Date): void {
        if (!value) {
            this._elementRef.nativeElement.value = "";
            return;
        }
    
        const type = this._elementRef.nativeElement.type;
    
        switch (type) {
            case "date":
                this._elementRef.nativeElement.value = formatDate(value, "yyyy-MM-dd", "en-US");
                break;
            case "datetime-local":
                this._elementRef.nativeElement.value = dayjs(value).utcOffset(-180).format("YYYY-MM-DDTHH:mm:ss");
                break;
        }
    
        this._patchInitialValue(value);
    }
    

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    private _listener() {
        if (!this._elementRef || !this._elementRef.nativeElement) return;

        this._elementRef.nativeElement.addEventListener('change', (event: any) => {
            const date = this._patchValue(event.target);
            this.onChange(date);
        });
    }

    private _verifyDate(value: any) {
        if (!value) {
            return false;
        }

        return !isNaN(Date.parse(value));
    }

    private _valueToApply(value: string | Date, type) {
        let date = new Date(new Date(value).toLocaleString("en-US", {timeZone: "UTC"}));
        
        if(typeof value === 'string') {
            date.setFullYear(parseInt(value.split('-')[0]));
        }

        if(type === 'date') {
            this._setCurrentTime(date);
        }

        if (type === "datetime-local") {
            this._clearTimezone(date);
        }

        return date;
    }

    private _patchValue(element: HTMLInputElement) {
        if (!element) return;

        return this._verifyDate(element.value) ? this._valueToApply(element.value, element.type) : undefined;
    }

    private _setCurrentTime(date: Date) {
        const now = new Date();
        this._clearTimezone(now);
        date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    }

    private _clearTimezone(date: Date) {
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    }

    private _patchInitialValue(value: Date) {
        if (!value) return;

        const date = new Date(new Date(value).toLocaleString("en-US", { timeZone: "America/Bahia" }));
        this._clearTimezone(date);
        this.onChange(date);
    }

    private _verifyDateTime(value: string) {
        const time = value.split(":").map(number => parseInt(number));
        const date = new Date();

        if(time.length != 2) return;

        date.setHours(time[0], time[1]);
        
        return date;
    }
}
